/**
 * Dialog on Radix. Implements DESIGN.md §Components > Dialog: a surface card, 20px
 * radius, 32px padding, the overlay shadow (shadow-3), a 24px/700 heading and a muted
 * 14px body, actions right-aligned over a navy 70 percent backdrop. Reference: stars.css
 * `.dialog` and `dialog.dialog::backdrop` (§Dialog). Radix owns focus trap, Escape,
 * outside click and focus return. The card is 512px (max-w-lg); the design's 520px has
 * no token.
 */
import React, { forwardRef } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { cn } from './cn';
import { SurfaceRoot, useSurfaceClass } from './surface';

export function Dialog({ surface, children, ...props }) {
  return (
    <RadixDialog.Root {...props}>
      <SurfaceRoot surface={surface}>{children}</SurfaceRoot>
    </RadixDialog.Root>
  );
}
export const DialogTrigger = RadixDialog.Trigger;
export const DialogClose = RadixDialog.Close;

export const DialogContent = forwardRef(function DialogContent({ className, children, ...props }, ref) {
  const surfaceClass = useSurfaceClass();
  return (
    <RadixDialog.Portal>
      {/* The overlay is also the centring box, so no inset fractions are needed. */}
      <RadixDialog.Overlay
        data-slot="dialog-overlay"
        className="tw-fixed tw-inset-0 tw-z-50 tw-grid tw-place-items-center tw-p-4 tw-bg-overlay-navy/70"
      >
        <RadixDialog.Content
          ref={ref}
          className={cn(
            'tw-w-full tw-max-w-lg tw-p-8 tw-rounded-lg tw-bg-surface tw-text-text tw-shadow-3',
            'focus:tw-outline-none',
            surfaceClass,
            className,
          )}
          {...props}
        >
          {children}
        </RadixDialog.Content>
      </RadixDialog.Overlay>
    </RadixDialog.Portal>
  );
});

export const DialogTitle = forwardRef(function DialogTitle({ className, ...props }, ref) {
  return <RadixDialog.Title ref={ref} className={cn('tw-mb-2 tw-text-xl tw-font-bold tw-text-text', className)} {...props} />;
});

export const DialogDescription = forwardRef(function DialogDescription({ className, ...props }, ref) {
  return <RadixDialog.Description ref={ref} className={cn('tw-text-sm tw-text-text-muted', className)} {...props} />;
});

export function DialogActions({ className, ...props }) {
  return <div className={cn('tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-3 tw-mt-6', className)} {...props} />;
}
